
import React, { useState, useEffect, useRef } from 'react';
import { BrowserMultiFormatReader, NotFoundException } from '@zxing/library';
import { Button } from '@/components/ui/button';
import { X, Flashlight, RotateCcw } from 'lucide-react';
import { useIsMobile } from '@/hooks/use-mobile';
import TShirtSizeModal from './components/TShirtSizeModal';
import { Attendee } from '@/types/attendee';
import ViewFinder from './components/ViewFinder';
import ScannerHeader from './components/ScannerHeader';
import ScannerError from './components/ScannerError';

interface CameraQrScannerProps {
  onQrDetected: (data: string) => void;
  onClose: () => void;
}

const CameraQrScanner: React.FC<CameraQrScannerProps> = ({ onQrDetected, onClose }) => {
  const [hasPermission, setHasPermission] = useState(false);
  const [decodedText, setDecodedText] = useState<string | null>(null);
  const [scannerError, setScannerError] = useState<string | null>(null);
  const [isScanning, setIsScanning] = useState(true);
  const [attendeeInfo, setAttendeeInfo] = useState<Attendee | null>(null);
  const [showTShirtModal, setShowTShirtModal] = useState(false);
  const [flashlightOn, setFlashlightOn] = useState(false);
  const [cameraStream, setCameraStream] = useState<MediaStream | null>(null);
  const [isSwitchingCamera, setIsSwitchingCamera] = useState(false);
  const [useFrontCamera, setUseFrontCamera] = useState(false);
  const [torchSupported, setTorchSupported] = useState(false);
  const [scanSuccess, setScanSuccess] = useState(false);
  
  const isMobile = useIsMobile();
  const videoRef = useRef<HTMLVideoElement>(null);
  const codeReaderRef = useRef<BrowserMultiFormatReader | null>(null);
  const scanSoundRef = useRef<HTMLAudioElement | null>(null);
  const closeTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  
  // Create audio element for scan sound
  useEffect(() => {
    // Create beep sound for successful scans
    scanSoundRef.current = new Audio("data:audio/wav;base64,UklGRl9vT19XQVZFZm10IBAAAAABAAEAQB8AAEAfAAABAAgAZGF0YU" + Array(300).join("123"));
    scanSoundRef.current.volume = 0.3;
    
    return () => {
      if (scanSoundRef.current) {
        scanSoundRef.current = null;
      }
      
      // Clear any pending timeouts on unmount
      if (closeTimeoutRef.current) {
        clearTimeout(closeTimeoutRef.current);
      }
    };
  }, []);
  
  // Request camera permission on mount
  useEffect(() => {
    const checkCameraPermission = async () => {
      try {
        const stream = await navigator.mediaDevices.getUserMedia({ 
          video: { 
            facingMode: useFrontCamera ? 'user' : 'environment',
            width: { ideal: 1280 },
            height: { ideal: 720 }
          } 
        });
        setHasPermission(true);
        setCameraStream(stream);
        
        // Check if torch is supported
        const videoTrack = stream.getVideoTracks()[0];
        if (videoTrack) {
          const capabilities = videoTrack.getCapabilities();
          // Check if the torch capability exists and is defined
          setTorchSupported('torch' in capabilities);
        }
        
        stream.getTracks().forEach(track => track.stop());
      } catch (error: any) {
        console.error("Camera permission denied:", error);
        setHasPermission(false);
        setScannerError("Quyền truy cập camera bị từ chối. Vui lòng bật quyền camera trong cài đặt trình duyệt của bạn.");
      }
    };
    
    checkCameraPermission();
  }, [useFrontCamera]);
  
  // Initialize and start the QR code scanner
  useEffect(() => {
    if (!hasPermission || !isScanning || scanSuccess) return;
    
    const startScanner = async () => {
      try {
        setIsSwitchingCamera(true);
        
        // Create new reader if it doesn't exist
        if (!codeReaderRef.current) {
          codeReaderRef.current = new BrowserMultiFormatReader();
        }
        
        // Get the video element
        const videoElement = videoRef.current;
        
        if (!videoElement) {
          console.error("Video element not found");
          setScannerError("Không tìm thấy phần tử video.");
          return;
        }
        
        // Reset the scanner
        await codeReaderRef.current.reset();
        
        // Start decoding from the video stream
        codeReaderRef.current.decodeFromConstraints(
          { 
            video: { 
              facingMode: useFrontCamera ? 'user' : 'environment',
              width: { ideal: 1280 },
              height: { ideal: 720 }
            } 
          },
          videoElement,
          (result, error) => {
            if (result && !scanSuccess) {
              // Set scan success flag to prevent multiple scans
              setScanSuccess(true);
              
              // QR code successfully decoded
              console.log('QR Code detected! - NO PAGE REFRESH', result.getText());
              setDecodedText(result.getText());
              setIsScanning(false);
              
              // Play sound on successful scan
              if (scanSoundRef.current) {
                scanSoundRef.current.play().catch(e => console.error("Error playing scan sound:", e));
              }
              
              // Provide haptic feedback on successful scan
              if ('vibrate' in navigator) {
                navigator.vibrate([100, 50, 100]);
              }
              
              // Call the onQrDetected callback
              onQrDetected(result.getText());
              
              // Schedule automatic close after scan success
              closeTimeoutRef.current = setTimeout(() => {
                console.log('Auto-closing scanner after successful scan - NO PAGE REFRESH');
                stopCameraAndClose();
              }, 800); // Short delay before closing
            }
            if (error && !(error instanceof NotFoundException)) {
              // Log the error, but only if it's not a "not found" error
              console.error('QR Code scanning error:', error);
              setScannerError('Lỗi khi quét mã QR. Vui lòng thử lại.');
            }
          }
        );
        
        console.log('QR Code scanner started - NO PAGE REFRESH');
        setScannerError(null); // Clear any previous errors
        setIsSwitchingCamera(false);
      } catch (error: any) {
        console.error("Error starting QR code scanner:", error);
        setScannerError("Lỗi khi khởi động máy quét mã QR: " + error.message);
        setIsSwitchingCamera(false);
      }
    };
    
    startScanner();
    
    // Clean up the scanner on unmount
    return () => {
      console.log('Stopping QR Code scanner - NO PAGE REFRESH');
      if (codeReaderRef.current) {
        codeReaderRef.current.reset();
      }
      if (cameraStream) {
        cameraStream.getTracks().forEach(track => track.stop());
      }
    };
  }, [hasPermission, onQrDetected, isScanning, useFrontCamera, cameraStream, scanSuccess, onClose]);
  
  // Function to stop camera and close the scanner
  const stopCameraAndClose = () => {
    // Stop camera stream if it exists
    if (cameraStream) {
      cameraStream.getTracks().forEach(track => track.stop());
    }
    
    // Stop scanner if it exists
    if (codeReaderRef.current) {
      codeReaderRef.current.reset();
    }
    
    // Call the onClose callback
    onClose();
  };
  
  // Listen for attendee-checked-in events to show t-shirt modal
  useEffect(() => {
    const handleAttendeeCheckedIn = (event: CustomEvent) => {
      if (event.detail && event.detail.attendee) {
        // Only show the T-shirt modal if the attendee was NOT already checked in
        if (event.detail.wasAlreadyCheckedIn !== true) {
          const attendee = event.detail.attendee;
          console.log('Attendee checked in event received - NO PAGE REFRESH:', attendee);
          setAttendeeInfo(attendee);
          setShowTShirtModal(true);
        }
        
        // Close the camera view regardless
        stopCameraAndClose();
      }
    };
    
    // Listen for the custom event
    window.addEventListener('attendee-checked-in', handleAttendeeCheckedIn as EventListener);
    
    return () => {
      window.removeEventListener('attendee-checked-in', handleAttendeeCheckedIn as EventListener);
    };
  }, [onClose]);
  
  // Toggle flashlight with proper type checking
  const toggleFlashlight = async () => {
    if (!cameraStream || !torchSupported) return;
    
    try {
      const videoTrack = cameraStream.getVideoTracks()[0];
      if (!videoTrack) return;
      
      const newFlashlightState = !flashlightOn;
      
      // Need to use 'as any' here because TypeScript definitions may not include torch
      // This is a browser compatibility issue where the torch feature exists but isn't in the type definitions
      await videoTrack.applyConstraints({
        advanced: [{ torch: newFlashlightState } as any]
      });
      
      setFlashlightOn(newFlashlightState);
    } catch (error) {
      console.error('Error toggling flashlight:', error);
    }
  };
  
  // Switch camera
  const switchCamera = () => {
    setUseFrontCamera(!useFrontCamera);
  };
  
  return (
    <div className="fixed inset-0 z-50 bg-black/90 flex flex-col items-center justify-start">
      <ScannerHeader onClose={stopCameraAndClose} />
      
      <div className="flex-1 w-full flex flex-col items-center justify-center relative">
        {/* Scanner */}
        {hasPermission ? (
          <div className="relative w-full max-w-md aspect-video overflow-hidden rounded-lg">
            <video 
              ref={videoRef}
              className="w-full h-full object-cover"
              style={{ display: hasPermission ? 'block' : 'none' }}
              autoPlay
              muted
              playsInline
            />
            
            {/* Scanning animation and viewfinder */}
            {isScanning && !scannerError && (
              <div className="absolute inset-0 flex items-center justify-center">
                <ViewFinder isScanning={isScanning} />
              </div>
            )}
            
            {/* Instructions */}
            <div className="absolute bottom-4 left-0 right-0 text-center">
              <p className="text-white text-sm font-medium bg-black/40 mx-auto max-w-xs rounded-full px-4 py-1 backdrop-blur-sm">
                Đặt mã QR vào trong khung
              </p>
            </div>
            
            {/* Camera controls */}
            <div className="absolute bottom-16 left-0 right-0 flex justify-center space-x-6">
              {/* Flashlight toggle - only show if supported */}
              {torchSupported && (
                <Button
                  variant="secondary"
                  size="icon"
                  className={`rounded-full ${flashlightOn ? 'bg-yellow-500 text-black' : 'bg-black/30 backdrop-blur-sm text-white'}`}
                  onClick={toggleFlashlight}
                  aria-label="Bật/tắt đèn flash"
                >
                  <Flashlight className="h-5 w-5" />
                </Button>
              )}
              
              {/* Camera switch */}
              <Button
                variant="secondary"
                size="icon"
                className="rounded-full bg-black/30 backdrop-blur-sm text-white"
                onClick={switchCamera}
                disabled={isSwitchingCamera}
                aria-label="Chuyển đổi camera"
              >
                <RotateCcw className={`h-5 w-5 ${isSwitchingCamera ? 'animate-spin' : ''}`} />
              </Button>
            </div>
            
            {/* Processing overlay */}
            {!isScanning && decodedText && (
              <div className="absolute inset-0 bg-green-500/20 backdrop-blur-sm flex items-center justify-center">
                <div className="bg-white rounded-lg p-4 text-center animate-scale-in">
                  <svg className="w-12 h-12 text-green-500 mx-auto" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                  </svg>
                  <p className="mt-2 font-medium">Mã QR đã được quét thành công!</p>
                </div>
              </div>
            )}
            
            {scannerError && (
              <div className="absolute inset-0 flex items-center justify-center bg-black/50 text-red-500 text-center p-4">
                <ScannerError errorMessage={scannerError} onClose={() => setScannerError(null)} />
              </div>
            )}
          </div>
        ) : (
          <div className="p-4 text-white text-center max-w-md">
            <svg className="w-16 h-16 text-red-500 mx-auto mb-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
            </svg>
            <h3 className="text-xl font-bold mb-2">Cần quyền truy cập camera</h3>
            <p className="mb-4">{scannerError || "Vui lòng cho phép ứng dụng truy cập camera của bạn để quét mã QR."}</p>
            <Button 
              variant="default" 
              onClick={() => window.location.reload()}
              className="bg-blue-600 hover:bg-blue-700"
            >
              Thử lại
            </Button>
          </div>
        )}
      </div>
      
      {/* T-shirt size modal - only show for new check-ins */}
      {attendeeInfo && showTShirtModal && (
        <TShirtSizeModal
          open={showTShirtModal}
          onClose={() => setShowTShirtModal(false)}
          attendeeName={attendeeInfo.name}
          tShirtSize={attendeeInfo.t_shirt_size}
          autoCloseDelay={3000} // Auto close after 3 seconds
        />
      )}
    </div>
  );
};

export default CameraQrScanner;
